import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Button, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Alert, CircularProgress, MenuItem, Select, FormControl, InputLabel,
  Checkbox, TablePagination,
} from '@mui/material';
import {
  Search, Refresh, Edit, Lock, Receipt, Person, Business, CheckCircle, Cancel,
} from '@mui/icons-material';
import { customersAdminAPI } from '../services/api';

const formatPhone = (v) => {
  const digits = (v || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits.slice(0, 11);
};

const formatMoney = (n) => n == null ? '-' : `${Number(n).toLocaleString()}원`;
const formatDate = (s) => s ? new Date(s).toLocaleDateString('ko-KR') : '-';

const STATUS_LABEL = {
  before_dispatch: '배차전', dispatch_ready: '배차준비', broadcasting: '호출중',
  assigned: '배차완료', in_progress: '배송중', completed: '완료', cancelled: '주문취소',
};
const PRICE_LABEL = {
  pending_price: '금액검토', payment_pending: '결재대기', deferred_payment: '추후결재',
  payment_completed: '결재완료', refund_pending: '환불대기', refund_completed: '환불완료',
  no_payment: '결재금액없음',
};

export default function CustomerList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [settlementFilter, setSettlementFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', 'active', 'pending'
  const [snack, setSnack] = useState({ type: '', msg: '' });
  // 서버측 페이지네이션
  const [page, setPage] = useState(0);       // 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // 수정 다이얼로그
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  // 비밀번호 변경 다이얼로그
  const [pwTarget, setPwTarget] = useState(null);
  const [pwInput, setPwInput] = useState('');
  // 거래실적 다이얼로그
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyData, setHistoryData] = useState({ orders: [], summary: null, loading: false });
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const load = (overrides = {}) => {
    setLoading(true);
    const params = {
      q: overrides.q !== undefined ? overrides.q : q,
      page: (overrides.page !== undefined ? overrides.page : page) + 1, // server는 1-indexed
      limit: overrides.limit !== undefined ? overrides.limit : rowsPerPage,
      settlement: overrides.settlement !== undefined ? overrides.settlement : settlementFilter,
      status: overrides.status !== undefined ? overrides.status : statusFilter,
    };
    customersAdminAPI.getAll(params)
      .then((res) => {
        setRows(res.data?.data || []);
        setTotal(res.data?.total || 0);
      })
      .catch(() => setSnack({ type: 'error', msg: '목록 로드 실패' }))
      .finally(() => setLoading(false));
  };
  // 마운트 + page/limit/settlement 변경 시 재조회
  useEffect(() => { load(); }, [page, rowsPerPage, settlementFilter, statusFilter]); // eslint-disable-line

  // 검색어는 debounce — 입력 멈춘 350ms 후 page 0 으로 재조회
  const debRef = useRef(null);
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      setPage(0);
      load({ page: 0, q });
    }, 350);
    return () => debRef.current && clearTimeout(debRef.current);
    // eslint-disable-next-line
  }, [q]);

  // 서버에서 이미 필터링/페이지네이션됨 — 클라이언트 필터 제거
  const filtered = rows;

  // 수정
  const openEdit = (row) => {
    setEditTarget(row);
    setEditForm({
      customer_type: row.customer_type,
      company_name: row.company_name || '',
      name: row.name || '',
      email: row.email || '',
      office_phone: formatPhone(row.office_phone || ''),
      // 사업자번호 자동 하이픈은 입력 시 onChange 에서 처리. 초기 표시값도 포맷:
      _bn_init: row.business_number, // (참고용)
      address: row.address || '',
      business_number: (() => {
        const d = (row.business_number || '').replace(/\D/g, '').slice(0, 10);
        if (d.length < 4) return d;
        if (d.length < 6) return `${d.slice(0,3)}-${d.slice(3)}`;
        return `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,10)}`;
      })(),
      ceo_name: row.ceo_name || '',
      settlement_method: row.settlement_method || '',
      is_active: !!row.is_active,
    });
  };
  const approveCustomer = async (row) => {
    if (!window.confirm(`'${row.company_name || row.name}' 가입을 승인할까요?`)) return;
    try {
      await customersAdminAPI.update(row.customer_id, { ...row, is_active: true });
      setSnack({ type: 'success', msg: '승인 완료. 고객이 로그인할 수 있습니다.' });
      load();
    } catch (e) {
      setSnack({ type: 'error', msg: e.response?.data?.message || '승인 실패' });
    }
  };

  const saveEdit = async () => {
    try {
      await customersAdminAPI.update(editTarget.customer_id, editForm);
      setSnack({ type: 'success', msg: '수정되었습니다.' });
      setEditTarget(null); load();
    } catch (e) {
      setSnack({ type: 'error', msg: e.response?.data?.message || '수정 실패' });
    }
  };

  // 비밀번호 변경
  const submitPassword = async () => {
    if (!pwInput || pwInput.length < 6) return setSnack({ type: 'error', msg: '6자 이상의 비밀번호를 입력하세요.' });
    try {
      await customersAdminAPI.changePassword(pwTarget.customer_id, pwInput);
      setSnack({ type: 'success', msg: `${pwTarget.name || pwTarget.company_name}님 비밀번호 변경됨` });
      setPwTarget(null); setPwInput('');
    } catch (e) {
      setSnack({ type: 'error', msg: e.response?.data?.message || '비밀번호 변경 실패' });
    }
  };

  // 거래실적 열기
  const openHistory = async (row) => {
    setHistoryTarget(row);
    setHistoryData({ orders: [], summary: null, loading: true });
    setSelectedOrderIds([]);
    try {
      const res = await customersAdminAPI.getOrders(row.customer_id);
      setHistoryData({ orders: res.data?.data || [], summary: res.data?.summary || null, loading: false });
    } catch {
      setHistoryData({ orders: [], summary: null, loading: false });
      setSnack({ type: 'error', msg: '거래실적 로드 실패' });
    }
  };

  const toggleOrderSelect = (id) => {
    setSelectedOrderIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const all = historyData.orders.map((o) => o.order_id);
    setSelectedOrderIds(selectedOrderIds.length === all.length ? [] : all);
  };

  // 선택된 거래 합계 (확정가 기준; 없으면 제시가)
  const selectedOrders = historyData.orders.filter((o) => selectedOrderIds.includes(o.order_id));
  const selectedTotal = selectedOrders.reduce((sum, o) => sum + (Number(o.confirmed_price) || Number(o.client_offered_price) || 0), 0);
  // 부가세 별도(10%) 가정: 표시 가격을 공급가액으로 간주하고 부가세 추가
  // (포함세로 처리하려면 confirmedPrice/1.1 분리 — 운영 정책에 따라 변경 가능)
  const supplyAmount = selectedTotal;
  const taxAmount = Math.round(supplyAmount * 0.1);
  const totalAmount = supplyAmount + taxAmount;

  // (세금계산서 발행은 직원포털의 '거래실적' 메뉴로 이관됨 — 여기서는 합계만 표시)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>고객 관리</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>상태</InputLabel>
            <Select value={statusFilter} label="상태" onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="pending">승인 대기</MenuItem>
              <MenuItem value="active">활성</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>결재</InputLabel>
            <Select value={settlementFilter} label="결재" onChange={(e) => { setPage(0); setSettlementFilter(e.target.value); }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="신용">신용</MenuItem>
              <MenuItem value="송금">송금</MenuItem>
              <MenuItem value="착불">착불</MenuItem>
              <MenuItem value="none">미지정</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small" placeholder="업체명/담당자/연락처/주소/사업자번호 검색"
            value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 320 }}
          />
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => load()}>새로고침</Button>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        총 {total.toLocaleString()}명 · 페이지 {page + 1} / {Math.max(1, Math.ceil(total / rowsPerPage))}
      </Typography>

      {snack.msg && <Alert severity={snack.type || 'info'} sx={{ mb: 2 }} onClose={() => setSnack({ type: '', msg: '' })}>{snack.msg}</Alert>}

      <Card>
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>구분</TableCell>
                <TableCell>업체명</TableCell>
                <TableCell>담당자</TableCell>
                <TableCell>연락처</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>사무실</TableCell>
                <TableCell>대표자</TableCell>
                <TableCell>사업자번호</TableCell>
                <TableCell>주소</TableCell>
                <TableCell>결재</TableCell>
                <TableCell>가입일</TableCell>
                <TableCell align="right">주문수</TableCell>
                <TableCell align="right">거래액</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="center">관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={15} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={15} align="center" sx={{ py: 4, color: 'text.secondary' }}>등록된 고객이 없습니다.</TableCell></TableRow>
              ) : filtered.map((r) => (
                <TableRow key={r.customer_id} hover>
                  <TableCell>
                    <Chip size="small"
                      icon={r.customer_type === 'corporate' ? <Business /> : <Person />}
                      label={r.customer_type === 'corporate' ? '기업' : '개인'}
                      color={r.customer_type === 'corporate' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{r.company_name || '-'}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{formatPhone(r.phone)}</TableCell>
                  <TableCell>{r.email || '-'}</TableCell>
                  <TableCell>{formatPhone(r.office_phone || '') || '-'}</TableCell>
                  <TableCell>{r.ceo_name || '-'}</TableCell>
                  <TableCell>{r.business_number ? (() => {
                    const d = String(r.business_number).replace(/\D/g, '');
                    return d.length === 10 ? `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,10)}` : r.business_number;
                  })() : '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.address || ''}>
                    {r.address || '-'}
                  </TableCell>
                  <TableCell>
                    {r.settlement_method
                      ? <Chip size="small" label={r.settlement_method}
                          color={r.settlement_method === '신용' ? 'primary' : r.settlement_method === '송금' ? 'success' : 'warning'} />
                      : '-'}
                  </TableCell>
                  <TableCell>{formatDate(r.created_at)}</TableCell>
                  <TableCell align="right">{r.order_count ?? 0}</TableCell>
                  <TableCell align="right">{formatMoney(r.total_amount)}</TableCell>
                  <TableCell>
                    {r.is_active
                      ? <Chip size="small" icon={<CheckCircle />} label="활성" color="success" />
                      : <Chip size="small" icon={<Cancel />} label="승인 대기" color="warning" />}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {!r.is_active && (
                      <IconButton size="small" color="success" title="가입 승인" onClick={() => approveCustomer(r)}>
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" title="정보 수정" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="warning" title="암호 변경" onClick={() => setPwTarget(r)}><Lock fontSize="small" /></IconButton>
                    <IconButton size="small" color="primary" title="거래실적" onClick={() => openHistory(r)}><Receipt fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="페이지당"
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} / 총 ${count.toLocaleString()}명`}
        />
      </Card>

      {/* 정보 수정 다이얼로그 */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>고객 정보 수정 {editTarget && `— ${editTarget.company_name || editTarget.name}`}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>구분</InputLabel>
                <Select label="구분" value={editForm.customer_type || 'corporate'}
                  onChange={(e) => setEditForm({ ...editForm, customer_type: e.target.value })}>
                  <MenuItem value="corporate">기업 고객</MenuItem>
                  <MenuItem value="personal">개인</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>상태</InputLabel>
                <Select label="상태" value={editForm.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'active' })}>
                  <MenuItem value="active">활성</MenuItem>
                  <MenuItem value="inactive">비활성</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="업체명" value={editForm.company_name || ''}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="담당자 이름 *" value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="대표자" value={editForm.ceo_name || ''}
                onChange={(e) => setEditForm({ ...editForm, ceo_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="사업자번호" value={editForm.business_number || ''}
                onChange={(e) => {
                  const d = (e.target.value || '').replace(/\D/g, '').slice(0, 10);
                  const v = d.length < 4 ? d
                    : d.length < 6 ? `${d.slice(0,3)}-${d.slice(3)}`
                    : `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,10)}`;
                  setEditForm({ ...editForm, business_number: v });
                }}
                inputProps={{ maxLength: 12 }}
                placeholder="000-00-00000" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="E-Mail" type="email" value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="사무실 연락처" value={editForm.office_phone || ''}
                onChange={(e) => setEditForm({ ...editForm, office_phone: formatPhone(e.target.value) })} />
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField fullWidth size="small" label="주소" value={editForm.address || ''}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>결재</InputLabel>
                <Select label="결재" value={editForm.settlement_method || ''}
                  onChange={(e) => setEditForm({ ...editForm, settlement_method: e.target.value })}>
                  <MenuItem value=""><em>(없음)</em></MenuItem>
                  <MenuItem value="신용">신용</MenuItem>
                  <MenuItem value="송금">송금</MenuItem>
                  <MenuItem value="착불">착불</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>취소</Button>
          <Button variant="contained" onClick={saveEdit}>저장</Button>
        </DialogActions>
      </Dialog>

      {/* 암호 변경 다이얼로그 */}
      <Dialog open={!!pwTarget} onClose={() => { setPwTarget(null); setPwInput(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>비밀번호 변경 {pwTarget && `— ${pwTarget.company_name || pwTarget.name}`}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            새 비밀번호를 직접 설정합니다. 변경 후 고객에게 안전하게 전달하세요.
          </Alert>
          <TextField fullWidth size="small" label="새 비밀번호 (6자 이상)" type="text"
            value={pwInput} onChange={(e) => setPwInput(e.target.value)} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPwTarget(null); setPwInput(''); }}>취소</Button>
          <Button variant="contained" color="warning" onClick={submitPassword}>변경</Button>
        </DialogActions>
      </Dialog>

      {/* 거래실적 다이얼로그 */}
      <Dialog open={!!historyTarget} onClose={() => setHistoryTarget(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          거래실적 {historyTarget && `— ${historyTarget.company_name || historyTarget.name}`}
        </DialogTitle>
        <DialogContent dividers>
          {historyData.loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <>
              {historyData.summary && (
                <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box><Typography variant="caption" color="text.secondary">총 주문 수</Typography>
                    <Typography variant="h6" fontWeight={700}>{historyData.summary.total_orders}건</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">완료 건수</Typography>
                    <Typography variant="h6" fontWeight={700} color="success.main">{historyData.summary.completed_count}건</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">총 거래액</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary">{formatMoney(historyData.summary.total_amount)}</Typography></Box>
                  <Box sx={{ flexGrow: 1 }} />
                  {selectedOrderIds.length > 0 && (
                    <Box sx={{ p: 1.5, bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 1 }}>
                      <Typography variant="caption" color="warning.dark" fontWeight={700}>선택 {selectedOrderIds.length}건</Typography>
                      <Typography variant="body2">공급가액 {formatMoney(supplyAmount)} · 부가세 {formatMoney(taxAmount)} · <strong>합계 {formatMoney(totalAmount)}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">※ 세금계산서 발행은 '거래실적' 메뉴에서</Typography>
                    </Box>
                  )}
                </Box>
              )}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedOrderIds.length > 0 && selectedOrderIds.length < historyData.orders.length}
                          checked={historyData.orders.length > 0 && selectedOrderIds.length === historyData.orders.length}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell>주문번호</TableCell>
                      <TableCell>생성일</TableCell>
                      <TableCell>출발</TableCell>
                      <TableCell>도착</TableCell>
                      <TableCell>차종</TableCell>
                      <TableCell align="right">제시가</TableCell>
                      <TableCell align="right">확정가</TableCell>
                      <TableCell>배차상태</TableCell>
                      <TableCell>결재상태</TableCell>
                      <TableCell>운전자</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.orders.length === 0 ? (
                      <TableRow><TableCell colSpan={11} align="center" sx={{ py: 3, color: 'text.secondary' }}>거래 내역이 없습니다.</TableCell></TableRow>
                    ) : historyData.orders.map((o) => {
                      const isSelected = selectedOrderIds.includes(o.order_id);
                      return (
                        <TableRow key={o.order_id} hover selected={isSelected}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => toggleOrderSelect(o.order_id)}>
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={isSelected} onChange={() => toggleOrderSelect(o.order_id)} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{o.order_number}</TableCell>
                          <TableCell>{formatDate(o.created_at)}</TableCell>
                          <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.origin_address}>{o.origin_address}</TableCell>
                          <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.destination_address}>{o.destination_address}</TableCell>
                          <TableCell>{o.required_vehicle_type || '-'}{o.required_weight ? ` (${o.required_weight}t)` : ''}</TableCell>
                          <TableCell align="right">{formatMoney(o.client_offered_price)}</TableCell>
                          <TableCell align="right">{formatMoney(o.confirmed_price)}</TableCell>
                          <TableCell><Chip size="small" label={STATUS_LABEL[o.status] || o.status} /></TableCell>
                          <TableCell><Chip size="small" label={PRICE_LABEL[o.price_status] || o.price_status || '-'} variant="outlined" /></TableCell>
                          <TableCell>{o.driver_name ? `${o.driver_name}${o.driver_phone ? ` (${formatPhone(o.driver_phone)})` : ''}` : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryTarget(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
